import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Shield } from "lucide-react";

const GDPRInformationSection = ({ data, onChange, brokerData }) => {
  const handleCheckboxChange = (field, checked) => {
    onChange({ [field]: checked });
  };

  // Date intermediar - completate automat din profilul brokerului
  const intermediaryName = brokerData?.representativeName || brokerData?.name || "[Numele Intermediarului]";
  const intermediaryCompany = brokerData?.companyName || brokerData?.companyData?.companyName || "[Denumirea firmei]";
  const intermediaryAddress = brokerData?.address || brokerData?.companyData?.address || "[Adresa completă]";
  const intermediaryEmail = brokerData?.email || "[Email intermediar]";
  const intermediaryPhone = brokerData?.phone || brokerData?.representativeData?.phone || "[Telefon intermediar]";

  return (
    <Card className="max-w-5xl mx-auto">
      <CardHeader className="bg-gradient-to-r from-green-600 to-green-800 text-white">
        <CardTitle className="flex items-center gap-2 text-2xl">
          <Shield className="h-6 w-6" />
          📗 GDPR – Informare privind protecția datelor cu caracter personal
        </CardTitle>
        <p className="text-green-100 text-sm mt-2">Intermediar ↔ Client (versiune extinsă, profesională)</p>
      </CardHeader>
      <CardContent className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
        
        {/* Secțiunea 1: Introducere */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">1. Introducere</h3>
          <p className="text-sm leading-relaxed text-gray-700">
            Prezenta Informare privind protecția datelor cu caracter personal („Informarea GDPR") descrie modul în care <strong>{intermediaryName}</strong> („Intermediarul") prelucrează datele cu caracter personal ale persoanelor fizice („Clientul" / "Persoana vizată") în contextul furnizării serviciilor de intermediere în domeniul creditelor bancare.
          </p>
          <p className="text-sm leading-relaxed text-gray-700">
            Această informare este conform cu Regulamentul (UE) 2016/679 („GDPR"), Legea nr. 190/2018 și legislația sectorială aplicabilă domeniului financiar-bancar.
          </p>
          <p className="text-sm leading-relaxed text-gray-700">
            Intermediarul acționează ca <strong>Operator de date</strong>, iar platforma <strong>FINBRO</strong> acționează exclusiv ca <strong>Împuternicit tehnic</strong>, fără a păstra sau procesa datele în mod independent.
          </p>
        </div>

        {/* Secțiunea 2: Cine suntem */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">2. Cine suntem</h3>
          <div className="bg-blue-50 p-4 rounded-lg space-y-2">
            <h4 className="font-semibold text-blue-900">Operator (Intermediar):</h4>
            <p className="text-sm text-gray-800"><strong>Nume Intermediar:</strong> {intermediaryName}</p>
            <p className="text-sm text-gray-800"><strong>Denumire firmă:</strong> {intermediaryCompany}</p>
            <p className="text-sm text-gray-800"><strong>Adresă:</strong> {intermediaryAddress}</p>
            <p className="text-sm text-gray-800"><strong>E-mail:</strong> {intermediaryEmail}</p>
            <p className="text-sm text-gray-800"><strong>Telefon:</strong> {intermediaryPhone}</p>
            <p className="text-xs text-blue-700 mt-2 italic">* Datele vor fi completate automat</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg space-y-2">
            <h4 className="font-semibold text-yellow-900">Împuternicit Tehnic (Platforma FINBRO):</h4>
            <p className="text-sm text-gray-800"><strong>FINBRO</strong> – platformă software pentru generarea și transmiterea formularelor.</p>
            <p className="text-sm text-gray-800 font-semibold">FINBRO NU stochează date și NU accesează datele după transmiterea PDF-ului către Intermediar.</p>
          </div>
        </div>

        {/* Secțiunea 3: Ce date prelucrăm */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">3. Ce date cu caracter personal prelucrăm</h3>
          <p className="text-sm leading-relaxed text-gray-700">
            În scopul identificării clientului, analizării eligibilității pentru credit și transmiterii informațiilor către instituțiile de credit, Intermediarul poate prelucra următoarele categorii de date:
          </p>
          
          <div className="space-y-3 pl-4">
            <div>
              <h4 className="font-semibold text-sm text-gray-900">3.1. Date de identificare</h4>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>nume, prenume</li>
                <li>CNP</li>
                <li>serie și număr CI/pașaport (dacă este necesar)</li>
                <li>data nașterii</li>
                <li>semnătura</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-gray-900">3.2. Date de contact</h4>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>număr de telefon</li>
                <li>adresă e-mail</li>
                <li>domiciliu / adresă corespondență</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-gray-900">3.3. Date profesionale și financiare</h4>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>angajator, venituri, tip venit</li>
                <li>istoricul de creditare</li>
                <li>date privind co-plătitori / soț / soție</li>
                <li>situația financiară declarată</li>
                <li>gradul de îndatorare</li>
                <li>informații primite de la ANAF sau Biroul de Credit (doar cu acordul dvs.)</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-gray-900">3.4. Documente încărcate de client</h4>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>act identitate</li>
                <li>adeverințe de venit</li>
                <li>extrase de cont</li>
                <li>contracte</li>
                <li>alte documente solicitate de banca aleasă</li>
              </ul>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-sm font-semibold text-red-900">IMPORTANT:</p>
            <p className="text-sm text-red-800">
              Platforma FINBRO NU stochează datele, nu păstrează copii ale documentelor. După transmiterea PDF-ului către Intermediar, informațiile sunt șterse automat.
            </p>
          </div>
        </div>

        {/* Secțiunea 4: Scopurile prelucrării */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">4. Scopurile prelucrării datelor</h3>
          <p className="text-sm leading-relaxed text-gray-700">
            Datele dvs. sunt prelucrate strict pentru:
          </p>
          <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 pl-4">
            <li>Analiza inițială a situației financiare și eligibilității pentru credit</li>
            <li>Transmiterea informațiilor către băncile partenere, numai cu acordul dvs.</li>
            <li>Comunicarea cu dvs. (telefonic sau prin e-mail)</li>
            <li>Întocmirea și transmiterea documentelor necesare cererii de credit</li>
            <li>Îndeplinirea obligațiilor legale (OUG 52/2016, AML/KYC, legislație financiară)</li>
            <li>Apărarea drepturilor intermediarului în cazul unui litigiu</li>
            <li>Arhivarea documentelor conform legislației financiare</li>
          </ol>
          <p className="text-sm leading-relaxed text-gray-700 font-semibold">
            Nicio prelucrare nu este realizată în scopuri comerciale, de marketing sau publicitate fără consimțământul dvs. explicit.
          </p>
        </div>

        {/* Secțiunea 5: Temeiul juridic */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">5. Temeiul juridic al prelucrării</h3>
          <p className="text-sm leading-relaxed text-gray-700">
            Prelucrarea datelor se bazează pe următoarele temeiuri:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 pl-4">
            <li><strong>Art. 6(1)(b) GDPR</strong> – executarea unui contract sau pași premergători contractului</li>
            <li><strong>Art. 6(1)(c) GDPR</strong> – îndeplinirea obligațiilor legale (AML, cerințe bancare, arhivare etc.)</li>
            <li><strong>Art. 6(1)(a) GDPR</strong> – consimțământ (pentru transmiterea către bancă)</li>
            <li><strong>Art. 6(1)(f) GDPR</strong> – interes legitim (protecția juridică a Intermediarului)</li>
          </ul>
          <p className="text-sm leading-relaxed text-gray-700 font-semibold">
            Intermediarul NU realizează profilare automată și NU ia decizii automate cu efect juridic.
          </p>
        </div>

        {/* Secțiunea 6: Cine primește datele */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">6. Cine primește datele dvs.</h3>
          <p className="text-sm leading-relaxed text-gray-700">
            Datele pot fi transmise doar către:
          </p>
          <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 pl-4">
            <li><strong>Băncile partenere</strong>, strict cu acordul dvs.</li>
            <li><strong>Instituții publice</strong>, în cazurile prevăzute de lege (ANAF, Notar, Biroul de Credit).</li>
            <li><strong>Împuternicitul tehnic (platforma FINBRO)</strong> – doar pentru generarea PDF-ului. → Platforma nu păstrează datele.</li>
            <li><strong>Contabili, avocați, auditori</strong> – doar dacă este necesar legal.</li>
          </ol>
          <p className="text-sm leading-relaxed text-gray-700 font-semibold">
            Datele nu sunt vândute, cedate sau folosite în scop publicitar.
          </p>
        </div>

        {/* Secțiunea 7: Durata păstrării */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">7. Durata păstrării datelor</h3>
          <p className="text-sm leading-relaxed text-gray-700">
            Intermediarul păstrează datele:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 pl-4">
            <li><strong>documente bancare și formulare</strong> – conform legislației financiar-bancare (5–10 ani)</li>
            <li><strong>documente fiscale/contabile</strong> – conform Codului Fiscal</li>
            <li><strong>date transmise prin platforma FINBRO</strong> – doar brokerul le păstrează; platforma nu le stochează</li>
          </ul>
          <p className="text-sm leading-relaxed text-gray-700">
            După expirarea perioadelor legale, datele sunt eliminate sau anonimizate.
          </p>
        </div>

        {/* Secțiunea 8: Securitatea datelor */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">8. Securitatea datelor</h3>
          <p className="text-sm leading-relaxed text-gray-700">
            Intermediarul implementează măsuri tehnice și organizatorice, precum:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 pl-4">
            <li>criptarea documentelor transmise</li>
            <li>acces restricționat doar pentru persoane autorizate</li>
            <li>parole complexe și autentificare la nivelul intermediarului</li>
            <li>audit intern și control periodic</li>
            <li>politici de confidențialitate semnate de personal</li>
            <li>infrastructură securizată pentru e-mail și arhivare</li>
          </ul>
          <div className="bg-gray-50 p-4 rounded-lg mt-2">
            <h4 className="font-semibold text-sm text-gray-900">Platforma FINBRO:</h4>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 mt-2">
              <li>nu reține date</li>
              <li>nu analizează date</li>
              <li>funcționează doar ca un generator temporar de PDF</li>
            </ul>
          </div>
        </div>

        {/* Secțiunea 9: Drepturile dumneavoastră */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-900">9. Drepturile dumneavoastră ca persoană vizată</h3>
          <p className="text-sm leading-relaxed text-gray-700">
            În conformitate cu GDPR, beneficiați de următoarele drepturi, pe care le puteți exercita oricând:
          </p>
          
          <div className="space-y-4 pl-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-sm text-gray-900">1. Dreptul de acces la date</h4>
              <p className="text-sm text-gray-700 mt-1">Aveți dreptul:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>să solicitați confirmarea că datele sunt prelucrate;</li>
                <li>să primiți o copie a datelor personale;</li>
                <li>să primiți clarificări privind: ce date colectăm, în ce scop, cui le-am transmis, cât timp le păstrăm, temeiul legal al prelucrării.</li>
              </ul>
              <p className="text-xs text-gray-600 mt-1">Solicitarea este gratuită, cu excepția copiilor suplimentare.</p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-sm text-gray-900">2. Dreptul la rectificare</h4>
              <p className="text-sm text-gray-700 mt-1">Puteți cere oricând:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>corectarea datelor inexacte;</li>
                <li>completarea datelor incomplete.</li>
              </ul>
              <p className="text-xs text-gray-600 mt-1">Intermediarul va transmite rectificările către destinatarii relevanți (ex.: bancă).</p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-sm text-gray-900">3. Dreptul la ștergerea datelor („dreptul de a fi uitat")</h4>
              <p className="text-sm text-gray-700 mt-1">Se aplică atunci când:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>datele nu mai sunt necesare scopului;</li>
                <li>vă retrageți consimțământul;</li>
                <li>datele au fost prelucrate ilegal;</li>
                <li>legea impune ștergerea.</li>
              </ul>
              <p className="text-xs text-red-600 mt-1 font-semibold">Atenție: datele NU pot fi șterse dacă legea obligă la păstrarea lor (ex.: AML/KYC, documente bancare).</p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-sm text-gray-900">4. Dreptul la restricționarea prelucrării</h4>
              <p className="text-sm text-gray-700 mt-1">Se aplică când:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>contestați acuratețea datelor;</li>
                <li>prelucrarea este ilegală;</li>
                <li>datele nu mai sunt necesare Intermediarului, dar le solicitați pentru instanță;</li>
                <li>v-ați opus prelucrării.</li>
              </ul>
              <p className="text-xs text-gray-600 mt-1">Pe durata restricționării, datele doar se stochează.</p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-sm text-gray-900">5. Dreptul la portabilitatea datelor</h4>
              <p className="text-sm text-gray-700 mt-1">Puteți solicita:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>furnizarea datelor într-un format tehnic (ex. XML, CSV);</li>
                <li>transmiterea directă către un alt operator (ex.: alt broker, bancă).</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-sm text-gray-900">6. Dreptul la opoziție</h4>
              <p className="text-sm text-gray-700 mt-1">Vă puteți opune:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>prelucrării bazate pe interes legitim;</li>
                <li>prelucrării în scopuri de marketing direct;</li>
                <li>profilării asociate interesului legitim.</li>
              </ul>
              <p className="text-xs text-gray-600 mt-1">Intermediarul poate continua prelucrarea doar dacă demonstrează un interes legitim superior.</p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-sm text-gray-900">7. Dreptul de retragere a consimțământului</h4>
              <p className="text-sm text-gray-700 mt-1">Puteți retrage acordul oricând, fără a afecta legalitatea prelucrărilor deja efectuate.</p>
              <p className="text-sm text-gray-700 mt-1 font-semibold">Consecințe:</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>înainte de trimiterea dosarului la bancă → procesul se oprește</li>
                <li>după trimitere → banca trebuie să păstreze datele conform legii</li>
              </ul>
            </div>

            <div className="border-l-4 border-red-500 pl-4 bg-red-50 p-3">
              <h4 className="font-semibold text-sm text-red-900">8. Dreptul de a depune o plângere</h4>
              <p className="text-sm text-gray-700 mt-1">Dacă considerați că v-au fost încălcate drepturile, puteți contacta:</p>
              <div className="bg-white p-3 rounded mt-2">
                <p className="text-sm font-semibold text-gray-900">ANSPDCP – Autoritatea Națională de Supraveghere a Prelucrării Datelor</p>
                <p className="text-xs text-gray-700">Website: www.dataprotection.ro</p>
                <p className="text-xs text-gray-700">E-mail: anspdcp@dataprotection.ro</p>
                <p className="text-xs text-gray-700">Adresă: Bd. G-ral Gh. Magheru nr. 28-30, București</p>
              </div>
              <p className="text-xs text-gray-600 mt-2">De asemenea, vă puteți adresa instanțelor competente.</p>
            </div>
          </div>
        </div>

        {/* Secțiunea 10: Platforma FINBRO */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">10. Platforma FINBRO – clarificări tehnice</h3>
          <p className="text-sm leading-relaxed text-gray-700">Platforma:</p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 pl-4">
            <li>nu păstrează date</li>
            <li>nu creează conturi pentru clienți</li>
            <li>nu arhivează documente</li>
            <li>nu accesează date după generarea PDF-ului</li>
            <li>nu face profilare, scorare, evaluare de credit</li>
          </ul>
          <p className="text-sm leading-relaxed text-gray-700 font-semibold mt-2">
            Toate responsabilitățile privind păstrarea datelor aparțin Intermediarului.
          </p>
        </div>

        {/* Secțiunea 11: Obligațiile Intermediarului */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">11. Obligațiile Intermediarului față de client</h3>
          <p className="text-sm leading-relaxed text-gray-700">Intermediarul:</p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 pl-4">
            <li>păstrează confidențialitatea datelor</li>
            <li>transmite datele doar cu consimțământ</li>
            <li>aplică măsuri de securitate</li>
            <li>respectă legislația financiar-bancară</li>
            <li>informează clientul despre orice incident de securitate</li>
            <li>permite exercitarea drepturilor GDPR</li>
          </ul>
        </div>

        {/* Secțiunea 12: Date de contact */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-gray-900">12. Date de contact pentru exercițiul drepturilor</h3>
          <p className="text-sm leading-relaxed text-gray-700">Pentru orice solicitare GDPR:</p>
          <div className="bg-blue-50 p-4 rounded-lg space-y-1">
            <p className="text-sm text-gray-800"><strong>E-mail Intermediar:</strong> {intermediaryEmail}</p>
            <p className="text-sm text-gray-800"><strong>Telefon Intermediar:</strong> {intermediaryPhone}</p>
            <p className="text-sm text-gray-800"><strong>Adresă completă:</strong> {intermediaryAddress}</p>
            <p className="text-xs text-blue-700 mt-2 italic">* Datele vor fi completate automat</p>
          </div>
        </div>

        {/* Checkbox-uri de acceptare */}
        <div className="border-t-2 border-gray-300 pt-6 mt-8 space-y-4">
          <h3 className="text-lg font-bold text-gray-900">Confirmări Obligatorii</h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 border-2 border-gray-300 rounded-lg">
              <Checkbox 
                id="gdprInformationRead"
                checked={data.gdprInformationRead || false}
                onCheckedChange={(checked) => handleCheckboxChange('gdprInformationRead', checked)}
                className="mt-1"
              />
              <Label htmlFor="gdprInformationRead" className="text-base font-medium cursor-pointer">
                Am citit și am înțeles toate cele 12 secțiuni ale Informării GDPR de mai sus
              </Label>
            </div>

            <div className="flex items-start space-x-3 p-4 border-2 border-gray-300 rounded-lg">
              <Checkbox 
                id="gdprConsentProcessing"
                checked={data.gdprConsentProcessing || false}
                onCheckedChange={(checked) => handleCheckboxChange('gdprConsentProcessing', checked)}
                className="mt-1"
              />
              <Label htmlFor="gdprConsentProcessing" className="text-base font-medium cursor-pointer">
                Sunt de acord cu prelucrarea datelor mele personale conform celor descrise mai sus
              </Label>
            </div>

            <div className="flex items-start space-x-3 p-4 border-2 border-gray-300 rounded-lg">
              <Checkbox 
                id="gdprRightsUnderstood"
                checked={data.gdprRightsUnderstood || false}
                onCheckedChange={(checked) => handleCheckboxChange('gdprRightsUnderstood', checked)}
                className="mt-1"
              />
              <Label htmlFor="gdprRightsUnderstood" className="text-base font-medium cursor-pointer">
                Înțeleg toate drepturile mele GDPR (acces, rectificare, ștergere, restricționare, portabilitate, opoziție, retragere consimțământ, plângere)
              </Label>
            </div>
          </div>
        </div>

      </CardContent>
    </Card>
  );
};

export default GDPRInformationSection;